import { useCallback, useMemo, useState, memo, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { FormField } from "@/components/ui/form-field";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import type {
  ProfessionalRegistrationData,
  SkillRecord,
} from "@/domain";
import type { UserInfo } from "@/types/core";
import { Input } from "@/components/ui/input";
import { cn, components, layout, typography } from "@/utilities/tailwind-utils";
import { NamePrefixSelect, CardSubHeader } from "@/components/ui";
import { MapLocationDialog, type MapLocation } from "@/components/maps";
import {
  validatePhoneNumber,
  formatPhoneNumber,
} from "@/utilities/validation-utils";
import {
  useAvailableStates,
  useAvailableCities,
  getAllCountries,
  resolveRegistrationAddress,
} from "@/utilities/location-utils";
import { toast } from "sonner";
import { useSpecialtiesFromLookupContext } from "@/context/use-lookups";
import {
  REFERRAL_CODE_LENGTH,
  isValidReferralCode,
  normalizeReferralCode,
  readStoredReferralCode,
} from "@/utilities/referral-utils";
import { RegistrationSkillPicker } from "./registration-skill-picker";

// TEMPORARY
import currencies from "@/assets/data/currencies.json";

// ============================================================================
// PROFESSIONAL REGISTRATION TYPES
// ============================================================================

interface ProfessionalRegistrationProps {
  data: ProfessionalRegistrationData;
  userInfo: UserInfo;
  onSubmit: (data: ProfessionalRegistrationData) => void;
  onBack: () => void;
  onCancel: () => void;
}

// ============================================================================
// PROFESSIONAL REGISTRATION COMPONENT
// ============================================================================

export const ProfessionalRegistration = memo(function ProfessionalRegistration({
  data,
  userInfo,
  onSubmit,
  onBack,
  onCancel,
}: ProfessionalRegistrationProps) {
  // Hooks
  const specialtiesLookup = useSpecialtiesFromLookupContext();

  // Initialize form data with memoized initial state - ensure all values are defined
  const initialFormData = useMemo<ProfessionalRegistrationData>(
    () => ({
      userId: userInfo.userId || data.userId || "",
      email: userInfo.email || data.email || "",
      namePrefix: data.namePrefix || "Dr.",
      firstName: userInfo.firstName || data.firstName || "",
      middleName: userInfo.middleName || data.middleName || "",
      lastName: userInfo.lastName || data.lastName || "",
      bio: data.bio || "",
      phone: data.phone || "",
      designation: data.designation || "",
      hourlyRateMin: data.hourlyRateMin || { amount: 0, currency: "CAD" },
      hourlyRateMax: data.hourlyRateMax || { amount: 0, currency: "CAD" },
      maxTravelDistance: data.maxTravelDistance || 50,
      licenseNumber: data.licenseNumber || "",
      yearsOfExperience: data.yearsOfExperience || "",
      skills: data.skills || [],
      specialties: data.specialties || [],
      addressCountry: data.addressCountry || "Canada",
      addressState: data.addressState || "",
      addressCity: data.addressCity || "",
      addressStreet: data.addressStreet || "",
      addressPostalCode: data.addressPostalCode || "",
      addressLongitude: data.addressLongitude,
      addressLatitude: data.addressLatitude,
      // Prefilled from the `?ref=` link the user arrived through, and editable
      // in case they were given a code by hand.
      referralCode: data.referralCode || readStoredReferralCode() || "",
    }),
    [data, userInfo]
  );

  // Split form states to prevent unnecessary re-renders
  const [namePrefix, setNamePrefix] = useState(
    () => initialFormData.namePrefix
  );
  const [firstName, setFirstName] = useState(() => initialFormData.firstName);
  const [middleName, setMiddleName] = useState(
    () => initialFormData.middleName
  );
  const [lastName, setLastName] = useState(() => initialFormData.lastName);
  const [phone, setPhone] = useState(() => initialFormData.phone);
  const [designation, setDesignation] = useState(
    () => initialFormData.designation
  );
  const [bio, setBio] = useState(() => initialFormData.bio);
  const [licenseNumber, setLicenseNumber] = useState(
    () => initialFormData.licenseNumber
  );
  const [yearsOfExperience, setYearsOfExperience] = useState(
    () => initialFormData.yearsOfExperience
  );
  const [referralCode, setReferralCode] = useState(
    () => initialFormData.referralCode
  );
  const [maxTravelDistance, setMaxTravelDistance] = useState(
    () => initialFormData.maxTravelDistance
  );
  const [addressStreet, setAddressStreet] = useState(
    () => initialFormData.addressStreet
  );
  const [addressPostalCode, setAddressPostalCode] = useState(
    () => initialFormData.addressPostalCode
  );
  const [addressCountry, setAddressCountry] = useState(
    () => initialFormData.addressCountry
  );
  const [addressState, setAddressState] = useState(
    () => initialFormData.addressState
  );
  const [addressCity, setAddressCity] = useState(
    () => initialFormData.addressCity
  );
  const [specialties, setSpecialties] = useState(
    () => initialFormData.specialties
  );
  const [skills, setSkills] = useState(() => initialFormData.skills);
  const [hourlyRateMin, setHourlyRateMin] = useState(
    () => initialFormData.hourlyRateMin
  );
  const [hourlyRateMax, setHourlyRateMax] = useState(
    () => initialFormData.hourlyRateMax
  );
  const [selectedCountry, setSelectedCountry] = useState(
    () => data.addressCountry || ""
  );
  const [phoneError, setPhoneError] = useState<string>("");
  const [hourlyRateError, setHourlyRateError] = useState<string>("");
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [selectedLocation, setSelectedLocation] = useState<MapLocation | null>(
    () =>
      initialFormData.addressLatitude != null &&
        initialFormData.addressLongitude != null
        ? {
          lat: initialFormData.addressLatitude,
          lng: initialFormData.addressLongitude,
        }
        : null
  );

  // Stable ref to hold complete form state for validation and submission
  const formDataRef = useRef<ProfessionalRegistrationData>(initialFormData);

  // Sync formDataRef when initialFormData changes
  useEffect(() => {
    formDataRef.current = initialFormData;
  }, [initialFormData]);

  // Validation function - works with stable ref
  const validateFields = useCallback(() => {
    const currentFormData = formDataRef.current;
    const requiredFields = [
      currentFormData.namePrefix?.trim(),
      currentFormData.firstName?.trim(),
      currentFormData.lastName?.trim(),
      currentFormData.phone?.trim(),
      currentFormData.designation?.trim(),
      currentFormData.licenseNumber?.trim(),
      currentFormData.yearsOfExperience?.trim(),
      currentFormData.specialties && currentFormData.specialties.length > 0
        ? currentFormData.specialties[0].specialtyId
        : "",
      currentFormData.skills && currentFormData.skills.length > 0
        ? currentFormData.skills[0].skillId
        : "",
      currentFormData.addressCountry?.trim(),
      currentFormData.addressState?.trim(),
      currentFormData.addressCity?.trim(),
      currentFormData.addressStreet?.trim(),
      currentFormData.addressPostalCode?.trim(),
    ];

    // Check if all required fields are filled
    const allFieldsFilled = requiredFields.every(
      (field) => field && field !== ""
    );

    // Check phone validation
    const phoneValidation = validatePhoneNumber(currentFormData.phone || "", {
      required: true,
      countryCode: currentFormData.addressCountry === "Canada" ? "CA" : "US",
    });

    const minAmount = currentFormData.hourlyRateMin?.amount ?? 0;
    const maxAmount = currentFormData.hourlyRateMax?.amount ?? 0;
    const ratesValid = minAmount > 0 && maxAmount > 0 && minAmount <= maxAmount;

    // Optional, but a malformed code is rejected by the API and would fail the
    // whole registration — catch it here instead of on the round-trip.
    const referral = currentFormData.referralCode?.trim() ?? "";
    const referralValid = referral === "" || isValidReferralCode(referral);

    return allFieldsFilled && phoneValidation.isValid && ratesValid && referralValid;
  }, []);

  // Debounced validation to update form validity state
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setIsFormValid(validateFields());
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [
    namePrefix,
    firstName,
    middleName,
    lastName,
    phone,
    designation,
    bio,
    licenseNumber,
    yearsOfExperience,
    referralCode,
    maxTravelDistance,
    addressCountry,
    addressState,
    addressCity,
    addressStreet,
    addressPostalCode,
    specialties,
    skills,
    hourlyRateMin,
    hourlyRateMax,
    phoneError,
    hourlyRateError,
    validateFields,
  ]);

  // Optimized field handlers - individual state updates with ref sync
  const handleNamePrefixChange = useCallback((value: string) => {
    setNamePrefix(value);
    formDataRef.current = { ...formDataRef.current, namePrefix: value };
  }, []);

  const handleFirstNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setFirstName(value);
      formDataRef.current = { ...formDataRef.current, firstName: value };
    },
    []
  );

  const handleMiddleNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setMiddleName(value);
      formDataRef.current = { ...formDataRef.current, middleName: value };
    },
    []
  );

  const handleLastNameChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLastName(value);
      formDataRef.current = { ...formDataRef.current, lastName: value };
    },
    []
  );

  const handlePhoneChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const countryCode = addressCountry === "Canada" ? "CA" : "US";

      // Format the phone number as user types
      const formattedValue = formatPhoneNumber(value, countryCode);
      setPhone(formattedValue);
      formDataRef.current = { ...formDataRef.current, phone: formattedValue };

      // Validate phone number
      const phoneValidation = validatePhoneNumber(formattedValue, {
        required: true,
        countryCode,
      });
      setPhoneError(phoneValidation.isValid ? "" : phoneValidation.error || "");
    },
    [addressCountry]
  );

  const handleDesignationChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setDesignation(value);
      formDataRef.current = { ...formDataRef.current, designation: value };
    },
    []
  );

  const handleBioChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const value = e.target.value;
      setBio(value);
      formDataRef.current = { ...formDataRef.current, bio: value };
    },
    []
  );

  const handleLicenseNumberChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setLicenseNumber(value);
      formDataRef.current = { ...formDataRef.current, licenseNumber: value };
    },
    []
  );

  const handleReferralCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // Normalized on the way in so the field always shows the canonical form
      // the API will match against.
      const value = normalizeReferralCode(e.target.value);
      setReferralCode(value);
      formDataRef.current = { ...formDataRef.current, referralCode: value };
    },
    []
  );

  const handleYearsOfExperienceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setYearsOfExperience(value);
      formDataRef.current = {
        ...formDataRef.current,
        yearsOfExperience: value,
      };
    },
    []
  );

  const handleMaxTravelDistanceChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      const numValue = parseInt(value) || 0;
      setMaxTravelDistance(numValue);
      formDataRef.current = {
        ...formDataRef.current,
        maxTravelDistance: numValue,
      };
    },
    []
  );

  const handleAddressStreetChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setAddressStreet(value);
      formDataRef.current = { ...formDataRef.current, addressStreet: value };
    },
    []
  );

  const handleAddressPostalCodeChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setAddressPostalCode(value);
      formDataRef.current = {
        ...formDataRef.current,
        addressPostalCode: value,
      };
    },
    []
  );

  const handleAddressCountryChange = useCallback((value: string) => {
    setAddressCountry(value);
    setAddressState("");
    setAddressCity("");
    setSelectedCountry(value);
    formDataRef.current = {
      ...formDataRef.current,
      addressCountry: value,
      addressState: "",
      addressCity: "",
    };
  }, []);

  const handleAddressStateChange = useCallback((value: string) => {
    setAddressState(value);
    setAddressCity("");
    formDataRef.current = {
      ...formDataRef.current,
      addressState: value,
      addressCity: "",
    };
  }, []);

  const handleAddressCityChange = useCallback((value: string) => {
    setAddressCity(value);
    formDataRef.current = { ...formDataRef.current, addressCity: value };
  }, []);

  const handleLocationSelect = useCallback((location: MapLocation) => {
    setSelectedLocation(location);
    const resolved = resolveRegistrationAddress(location, {
      addressStreet: formDataRef.current.addressStreet || "",
      addressCity: formDataRef.current.addressCity || "",
      addressState: formDataRef.current.addressState || "",
      addressPostalCode: formDataRef.current.addressPostalCode || "",
      addressCountry: formDataRef.current.addressCountry || "",
    });
    setAddressStreet(resolved.addressStreet);
    setAddressPostalCode(resolved.addressPostalCode);
    setAddressCountry(resolved.addressCountry);
    setSelectedCountry(resolved.addressCountry);
    setAddressState(resolved.addressState);
    setAddressCity(resolved.addressCity);
    formDataRef.current = {
      ...formDataRef.current,
      ...resolved,
      addressLatitude: location.lat,
      addressLongitude: location.lng,
    };
  }, []);

  const handleSpecialtiesChange = useCallback((value: string) => {
    const newSpecialties = [{ specialtyId: value, yearsOfExperience: 2 }];
    setSpecialties(newSpecialties);
    formDataRef.current = {
      ...formDataRef.current,
      specialties: newSpecialties,
    };
  }, []);

  const handleSkillsChange = useCallback((newSkills: SkillRecord[]) => {
    setSkills(newSkills);
    formDataRef.current = { ...formDataRef.current, skills: newSkills };
  }, []);

  const handleHourlyRateMinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const amount = e.target.value === "" ? 0 : Number(e.target.value);
      const newRateMin = {
        amount: Number.isFinite(amount) ? amount : 0,
        currency: hourlyRateMin?.currency || hourlyRateMax?.currency || "CAD",
      };
      setHourlyRateMin(newRateMin);
      formDataRef.current = {
        ...formDataRef.current,
        hourlyRateMin: newRateMin,
      };
    },
    [hourlyRateMin?.currency, hourlyRateMax?.currency]
  );

  const handleHourlyRateMinCurrencyChange = useCallback(
    (value: string) => {
      const newRateMin = {
        amount: hourlyRateMin?.amount ?? 0,
        currency: value,
      };
      const newRateMax = {
        amount: hourlyRateMax?.amount ?? 0,
        currency: value,
      };
      setHourlyRateMin(newRateMin);
      setHourlyRateMax(newRateMax);
      formDataRef.current = {
        ...formDataRef.current,
        hourlyRateMin: newRateMin,
        hourlyRateMax: newRateMax,
      };
    },
    [hourlyRateMin?.amount, hourlyRateMax?.amount]
  );

  const handleHourlyRateMaxChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const amount = e.target.value === "" ? 0 : Number(e.target.value);
      const newRateMax = {
        amount: Number.isFinite(amount) ? amount : 0,
        currency: hourlyRateMax?.currency || hourlyRateMin?.currency || "CAD",
      };
      setHourlyRateMax(newRateMax);
      formDataRef.current = {
        ...formDataRef.current,
        hourlyRateMax: newRateMax,
      };
    },
    [hourlyRateMax?.currency, hourlyRateMin?.currency]
  );

  // Optimized submit handler
  const handleSubmit = useCallback(() => {
    // Basic validation using the validation function
    const minAmount = formDataRef.current.hourlyRateMin?.amount ?? 0;
    const maxAmount = formDataRef.current.hourlyRateMax?.amount ?? 0;
    if (minAmount <= 0 || maxAmount <= 0) {
      setHourlyRateError(
        "Minimum and maximum hourly rates must be greater than 0."
      );
      toast.error("Minimum and maximum hourly rates must be greater than 0.");
      return;
    }
    if (minAmount > maxAmount) {
      setHourlyRateError(
        "Minimum hourly rate cannot be greater than maximum hourly rate."
      );
      toast.error(
        "Minimum hourly rate cannot be greater than maximum hourly rate."
      );
      return;
    }
    setHourlyRateError("");

    if (!validateFields()) {
      toast.error("Please fill in all required fields");
      return;
    }

    // Submit form data from ref
    onSubmit(formDataRef.current);
  }, [onSubmit, validateFields]);

  // Get available states and cities using utilities
  const availableStates = useAvailableStates(selectedCountry);
  const availableCities = useAvailableCities(selectedCountry, addressState);
  const countries = useMemo(() => getAllCountries(), []);

  // Render

  return (
    <div className="space-y-8">
      {/* PERSONAL INFORMATION SECTION */}
      <div className="space-y-6">
        <CardSubHeader title="PERSONAL INFORMATION" />

        <div className="space-y-4">
          {/* NAME PREFIX AND FIRST NAME */}
          <div className={cn(layout.flexRowGap2ColSpan1)}>
            {/* Name Prefix */}
            <NamePrefixSelect
              value={namePrefix}
              onChange={handleNamePrefixChange}
              required
            />
            {/* FIRST NAME */}
            <FormField
              id="firstName"
              label="First Name"
              value={firstName}
              onChange={handleFirstNameChange}
              placeholder="e.g. John"
              maxLength={20}
              required
              className="w-full"
            />
          </div>

          {/* MIDDLE NAME AND LAST NAME */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* MIDDLE NAME */}
            <FormField
              id="middleName"
              label="Middle Name"
              value={middleName}
              onChange={handleMiddleNameChange}
              placeholder="Middle Name"
              maxLength={25}
              className="col-span-1"
            />
            {/* LAST NAME */}
            <FormField
              id="lastName"
              label="Last Name"
              value={lastName}
              onChange={handleLastNameChange}
              placeholder="e.g. Doe"
              maxLength={20}
              required
              className="col-span-1"
            />
          </div>

          {/* PHONE NUMBER AND EMAIL */}
          <div className={cn(layout.gridCols1MdCols2)}>
            {/* PHONE NUMBER */}
            <div className="col-span-1">
              <FormField
                id="phone"
                label="Mobile number"
                value={phone}
                onChange={handlePhoneChange}
                placeholder="+1 412 869 4134"
                type="tel"
                maxLength={20}
                className={phoneError ? "border-destructive" : ""}
              />
              {phoneError && (
                <p className={cn(typography.phoneErrorMessage)}>{phoneError}</p>
              )}
            </div>

            {/* EMAIL */}
            <FormField
              id="email"
              label="Email address"
              value={initialFormData.email}
              placeholder="e.g. johndoe@email.com"
              disabled
              required
              className="col-span-1"
            />
          </div>

          {/* CURRENT ADDRESS */}
          <div className="space-y-2">
            <Label htmlFor="address">Current Address</Label>
            <div className={layout.flexCenterGap2}>
              <div className={layout.flex1}>
                <FormField
                  id="address"
                  value={addressStreet}
                  onChange={handleAddressStreetChange}
                  placeholder="Start typing your address"
                  maxLength={255}
                  required
                  className="flex-1"
                />
              </div>
              <MapLocationDialog
                onLocationSelect={handleLocationSelect}
                initialLocation={selectedLocation}
                defaultCenter={
                  selectedLocation
                    ? { lat: selectedLocation.lat, lng: selectedLocation.lng }
                    : undefined
                }
              />
            </div>
            <p className="text-sm text-muted-foreground">
              Adding your address helps us match you with shifts nearby
            </p>
          </div>

          {/* COUNTRY AND STATE */}
          <div className={cn(layout.gridCols1MdCols2)}>
            <div className="space-y-2">
              <Label htmlFor="country">Country</Label>
              <Select
                value={addressCountry}
                onValueChange={handleAddressCountryChange}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((country, idx) => (
                    <SelectItem key={country.name + idx} value={country.name}>
                      {country.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="state">State</Label>
              <Select
                value={addressState}
                onValueChange={handleAddressStateChange}
                disabled={!addressCountry}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select state" />
                </SelectTrigger>
                <SelectContent>
                  {availableStates.map((state) => (
                    <SelectItem
                      key={state.code + state.name}
                      value={state.code}
                    >
                      {state.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* CITY AND ZIP CODE */}
          <div className={cn(layout.gridCols1MdCols2)}>
            <div className="space-y-2">
              <Label htmlFor="city">City</Label>
              <Select
                value={addressCity}
                onValueChange={handleAddressCityChange}
                disabled={!addressState}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select city" />
                </SelectTrigger>
                <SelectContent>
                  {availableCities.map((city) => (
                    <SelectItem key={city.code} value={city.name}>
                      {city.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <FormField
              id="zipCode"
              label="ZIP Code"
              value={addressPostalCode}
              onChange={handleAddressPostalCodeChange}
              placeholder="Enter ZIP code"
              maxLength={20}
              required
            />
          </div>
        </div>
      </div>

      {/* PROFESSIONAL PROFILE SECTION */}
      <div className="space-y-6 mb-0">
        <CardSubHeader title="PROFESSIONAL PROFILE" />

        <div className="space-y-4">
          {/* BIO */}
          <div className="space-y-2">
            <Label htmlFor="bio">Bio</Label>
            <Textarea
              id="bio"
              value={bio || ""}
              onChange={handleBioChange}
              placeholder="e.g. Experienced Dental Assistant with strong chairside skills and Invisalign certification"
              maxLength={1000}
              className="min-h-[100px]"
              required
            />
            <div className="text-right text-xs text-gray-500">
              {bio?.length || 0}/1000
            </div>
          </div>

          <div className={cn(layout.gridCols1MdCols2)}>
            {/* LICENSE NUMBER */}
            <FormField
              id="licenseNumber"
              label="License Number"
              value={licenseNumber}
              onChange={handleLicenseNumberChange}
              placeholder="Enter license number"
              maxLength={100}
              className="col-span-1"
              required
            />
            {/* YEARS OF EXPERIENCE */}
            <FormField
              id="yearsOfExperience"
              label="Years of Experience"
              value={yearsOfExperience}
              onChange={handleYearsOfExperienceChange}
              placeholder="Enter years of experience"
              maxLength={100}
              className="col-span-1"
              required
            />
          </div>

          {/* DESIGNATION AND MAX TRAVEL DISTANCE */}
          <div className="grid grid-cols-2 gap-4">
            {/* DESIGNATION */}
            <FormField
              id="designation"
              label="Designation"
              value={designation}
              onChange={handleDesignationChange}
              placeholder="e.g. Dentist, Dental Hygienist, Dental Assistant, etc."
              maxLength={255}
              required
              className="col-span-1"
            />

            {/* MAX TRAVEL DISTANCE */}
            <FormField
              id="maxTravelDistance"
              label="Max Travel Distance (km)"
              value={maxTravelDistance}
              onChange={handleMaxTravelDistanceChange}
              placeholder="Enter maximum travel distance (km)"
              type="number"
              min={10}
              max={1000}
              maxLength={4}
              required
              className="col-span-1"
            />
          </div>

          {/* REFERRAL CODE */}
          <div className="space-y-2">
            <FormField
              id="referralCode"
              label="Referral Code (optional)"
              value={referralCode ?? ""}
              onChange={handleReferralCodeChange}
              placeholder="e.g. K7WQ4TZM"
              maxLength={REFERRAL_CODE_LENGTH}
            />
            {referralCode && !isValidReferralCode(referralCode) ? (
              <p className="text-sm text-destructive">
                Referral codes are {REFERRAL_CODE_LENGTH} characters and never
                contain I, O, 0 or 1.
              </p>
            ) : (
              <p className="text-sm text-muted-foreground">
                Invited by a colleague? Enter their code so they get credit.
              </p>
            )}
          </div>

          {/* Specialties */}
          <div className="space-y-2">
            <Label htmlFor="specialties">Select the roles you're open to</Label>
            <Select
              value={specialties?.[0]?.specialtyId || ""}
              onValueChange={handleSpecialtiesChange}
              disabled={!specialtiesLookup || !specialtiesLookup.length}
              required
            >
              <SelectTrigger className={cn(components.selectTriggerFormField)}>
                <SelectValue placeholder="Select roles" />
              </SelectTrigger>
              <SelectContent>
                {specialtiesLookup &&
                  specialtiesLookup.map((specialty) => (
                    <SelectItem key={specialty.id} value={specialty.id}>
                      {specialty.title}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-muted-foreground">
              Choose all the roles you're qualified and willing to work in. This
              helps us match you with the right shifts.
            </p>
          </div>

          {/* SKILLS */}
          <RegistrationSkillPicker
            value={skills ?? []}
            onChange={handleSkillsChange}
          />

          {/* HOURLY RATE */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* MINIMUM HOURLY RATE */}
            <div className="space-y-2">
              <Label htmlFor="hourlyRateMin">Minimum Hourly Rate</Label>
              <Input
                id="hourlyRateMin"
                value={
                  hourlyRateMin?.amount && hourlyRateMin.amount > 0
                    ? hourlyRateMin.amount
                    : ""
                }
                onChange={handleHourlyRateMinChange}
                placeholder="Enter minimum hourly rate"
                type="number"
                min={0.01}
                step="0.01"
                required
              />
            </div>

            {/* MAXIMUM HOURLY RATE */}
            <div className="space-y-2">
              <Label htmlFor="hourlyRateMax">Maximum Hourly Rate</Label>
              <Input
                id="hourlyRateMax"
                value={
                  hourlyRateMax?.amount && hourlyRateMax.amount > 0
                    ? hourlyRateMax.amount
                    : ""
                }
                onChange={handleHourlyRateMaxChange}
                placeholder="Enter maximum hourly rate"
                type="number"
                min={0.01}
                step="0.01"
                required
              />
            </div>

            {/* CURRENCY */}
            <div className="space-y-2">
              <Label>Currency</Label>
              <Select
                value={hourlyRateMin?.currency || "CAD"}
                onValueChange={handleHourlyRateMinCurrencyChange}
                required
              >
                <SelectTrigger
                  className={cn(components.selectTriggerFormField)}
                >
                  <SelectValue placeholder="Select currency" />
                </SelectTrigger>
                <SelectContent>
                  {currencies.map((currency) => (
                    <SelectItem key={currency.code} value={currency.code}>
                      {currency.code}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          {hourlyRateError ? (
            <p className="text-sm text-destructive">{hourlyRateError}</p>
          ) : null}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex justify-between pt-6">
        <Button variant="outline" onClick={onBack} className="px-6">
          Back
        </Button>
        <div className="flex space-x-3">
          <Button variant="outline" onClick={onCancel} className="px-6">
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            className={cn(components.buttonContinue)}
            disabled={!isFormValid}
          >
            CONTINUE
          </Button>
        </div>
      </div>
    </div>
  );
});
